/**
 * @file Builds the funding-unlocked toggle item shown in the funding descriptions.
 */
import { useState } from "react";
import { Switch } from "antd";
import type { DescriptionsProps } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { updateInvestmentOperation } from "@api/services/investments";

import type { Investment, ReadInvestment } from "../../../../types";

const LABEL_ALLOW_NO_TIN = "Allow to fund without TIN";

/**
 * Builds the "Allow to fund without TIN" toggle item for an investment's funding descriptions.
 */
const useItems = (investment: Investment, readInvestment: ReadInvestment): DescriptionsProps["items"] => {
  const { request } = useAppContext() as AppContext;
  const [ isLoading, setIsLoading ] = useState(false);

  const { id, isFundingUnlocked } = investment;

  const mutation = { isFundingUnlocked: !isFundingUnlocked };

  /**
   * Toggles the funding-unlocked flag and reloads the investment.
   */
  const onChange = () => {
    setIsLoading(true);
    return request(updateInvestmentOperation, { id, mutation })
      .then(() => readInvestment(id))
      .then(() => setIsLoading(false));
  };

  const items: DescriptionsProps["items"] = [
    {
      label: LABEL_ALLOW_NO_TIN,
      children: (
        <Switch
          loading={isLoading}
          checked={isFundingUnlocked}
          onChange={onChange}
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
        />
      ),
    },
  ];

  return items;
};

export default useItems;
